import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plane, Search, Calendar as CalendarIcon } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAmadeusApi, FlightOffer } from '@/hooks/useAmadeusApi';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

// Função auxiliar para formatar datas com segurança
const safeFormatDate = (dateString: string | undefined, formatStr: string, defaultValue: string = 'N/A') => {
  if (!dateString) return defaultValue;
  
  try {
    const date = parseISO(dateString);
    // Verificar se a data é válida (não é NaN)
    if (!isValid(date)) return defaultValue;
    return format(date, formatStr, { locale: ptBR });
  } catch (e) {
    return defaultValue;
  }
};

// Função auxiliar para formatar data
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  
  try {
    const date = parseISO(dateString);
    return format(date, 'dd/MM/yyyy');
  } catch (e) {
    return '';
  }
};

export default function FlightSearch() {
  const { toast } = useToast();
  const { isConfigured, searchFlightByNumber, searchFlightOffers } = useAmadeusApi();
  
  // Estados para a busca por número de voo
  const [flightNumber, setFlightNumber] = useState('');
  const [flightDate, setFlightDate] = useState<Date | undefined>(undefined);
  
  // Estados para a busca por rota
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departureDate, setDepartureDate] = useState<Date | undefined>(undefined);
  
  // Estados compartilhados
  const [isSearching, setIsSearching] = useState(false);
  const [flightResults, setFlightResults] = useState<FlightOffer[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  const handleSearchByNumber = async () => {
    if (!flightNumber || flightNumber.trim().length < 3) {
      setSearchError('Por favor, insira um número de voo válido');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setFlightResults([]);
    
    try {
      toast({
        title: "Buscando voo",
        description: `Buscando informações para o voo ${flightNumber}${flightDate ? ` em ${format(flightDate, 'dd/MM/yyyy')}` : ''}`,
      });
      
      const formattedFlightNumber = formatFlightNumber(flightNumber);
      
      const searchDate = flightDate ? format(flightDate, 'yyyy-MM-dd') : undefined;
      
      const results = await searchFlightByNumber(formattedFlightNumber, searchDate);
      
      if (!results || results.length === 0) {
        setSearchError('Nenhum voo encontrado com os parâmetros informados.');
        setFlightResults([]);
        toast({
          title: "Nenhum voo encontrado",
          description: "Não foram encontrados voos com os parâmetros informados.",
          variant: "destructive"
        });
      } else {
        setFlightResults(results);
        toast({
          title: "Busca concluída",
          description: `Foram encontrados ${results.length} voos.`,
        });
      }
    } catch (error) {
      setSearchError('Não foi possível obter informações do voo. Tente novamente mais tarde.');
      toast({
        title: "Erro na busca",
        description: "Não foi possível obter informações do voo. Tente novamente mais tarde.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleSearchByRoute = async () => {
    if (!origin || !destination) {
      toast({
        title: "Origem e destino necessários",
        description: "Por favor, informe a origem e o destino para realizar a busca.",
        variant: "destructive"
      });
      return;
    }
    
    if (!departureDate) {
      toast({
        title: "Data de partida necessária",
        description: "Por favor, informe a data de partida para realizar a busca.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    setFlightResults([]);
    
    try {
      // Formatar a data para o formato YYYY-MM-DD
      const formattedDate = format(departureDate, 'yyyy-MM-dd');
      const results = await searchFlightOffers(undefined, undefined, formattedDate, origin, destination);
      
      setFlightResults(results);
      
      if (results.length === 0) {
        setSearchError('Nenhum voo encontrado com os critérios informados.');
        toast({
          title: "Nenhum resultado",
          description: "Não foram encontrados voos com os critérios informados.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Busca concluída",
          description: `Foram encontrados ${results.length} voos.`,
        });
      }
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Erro ao buscar informações do voo.');
      toast({
        title: "Erro na busca",
        description: err instanceof Error ? err.message : 'Ocorreu um erro ao buscar informações do voo.',
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };
  
  const getAirlineName = (carrierCode: string) => {
    // Mapeamento básico de códigos de companhias aéreas para nomes
    const airlines: Record<string, string> = {
      'LA': 'LATAM Airlines',
      'G3': 'GOL Linhas Aéreas',
      'AD': 'Azul Linhas Aéreas',
      'AA': 'American Airlines',
      'DL': 'Delta Air Lines',
      'UA': 'United Airlines',
      'BA': 'British Airways',
      'LH': 'Lufthansa',
      'AF': 'Air France',
      'KL': 'KLM',
      'IB': 'Iberia',
      'AZ': 'Alitalia',
      'EK': 'Emirates',
      'QR': 'Qatar Airways',
      'EY': 'Etihad Airways',
      'TK': 'Turkish Airlines',
      'SQ': 'Singapore Airlines',
      'CX': 'Cathay Pacific',
      'JL': 'Japan Airlines',
      'NH': 'ANA All Nippon Airways',
    };
    
    return airlines[carrierCode] || carrierCode;
  };
  
  const formatDuration = (minutes: number) => {
    try {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    } catch (e) {
      return '';
    }
  };
  
  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">Busca de Voos - Teste da API Amadeus</h1>
      
      {!isConfigured ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <h2 className="text-lg font-semibold text-red-700 mb-2">API não configurada</h2>
          <p className="text-red-600">
            A API da Amadeus não está configurada. Por favor, configure as credenciais da API nas configurações.
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.href = '/admin/settings'}
          >
            Ir para Configurações
          </Button>
        </div>
      ) : (
        <Tabs defaultValue="number" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="number">Buscar por Número de Voo</TabsTrigger>
            <TabsTrigger value="route">Buscar por Rota</TabsTrigger>
          </TabsList>
          
          <TabsContent value="number" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Busca por Número de Voo</CardTitle>
                <CardDescription>
                  Informe o número do voo e, opcionalmente, a data para buscar informações.
                  Use o formato XX1234 (código da companhia aérea seguido do número do voo).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="flight-number">Número do Voo</Label>
                    <Input
                      id="flight-number"
                      placeholder="Ex: LA8084, G37654"
                      value={flightNumber}
                      onChange={(e) => setFlightNumber(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="flight-date">Data do Voo (opcional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="flight-date"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !flightDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {flightDate ? (
                            format(flightDate, "dd/MM/yyyy")
                          ) : (
                            <span>Selecionar data</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={flightDate}
                          onSelect={setFlightDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleSearchByNumber} 
                  disabled={isSearching || !flightNumber}
                  className="w-full"
                >
                  {isSearching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Buscar Voo
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="route" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Busca por Rota</CardTitle>
                <CardDescription>
                  Informe a origem, destino e data para buscar voos disponíveis.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="origin">Origem (código IATA)</Label>
                    <Input
                      id="origin"
                      placeholder="Ex: GRU, CGH"
                      value={origin}
                      onChange={(e) => setOrigin(e.target.value.toUpperCase())}
                      maxLength={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destino (código IATA)</Label>
                    <Input
                      id="destination"
                      placeholder="Ex: SDU, GIG"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value.toUpperCase())}
                      maxLength={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="departure-date">Data de Partida</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="departure-date"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !departureDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {departureDate ? (
                            format(departureDate, "dd/MM/yyyy")
                          ) : (
                            <span>Selecionar data</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={departureDate}
                          onSelect={setDepartureDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleSearchByRoute} 
                  disabled={isSearching || !origin || !destination || !departureDate}
                  className="w-full"
                >
                  {isSearching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Buscar Voos
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      )}
      
      {/* Resultados da busca */}
      {flightResults.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Resultados da Busca</h2>
          <div className="grid grid-cols-1 gap-4">
            {flightResults.map((flight, index) => {
              // Extrair informações do primeiro segmento do primeiro itinerário
              const firstItinerary = flight.itineraries[0];
              const firstSegment = firstItinerary.segments[0];
              const lastSegment = firstItinerary.segments[firstItinerary.segments.length - 1];
              
              const carrierCode = firstSegment.carrierCode;
              const flightNumber = `${carrierCode}${firstSegment.number}`;
              const airlineName = getAirlineName(carrierCode);
              
              const departureAirport = firstSegment.departure.iataCode;
              const departureTime = firstSegment.departure.at;
              
              const arrivalAirport = lastSegment.arrival.iataCode;
              const arrivalTime = lastSegment.arrival.at;
              
              const stops = firstItinerary.segments.length - 1;
              
              // Informações de preço
              const price = flight.price?.total;
              const currency = flight.price?.currency || 'EUR';
              
              // Calcular duração do voo se não estiver disponível
              let duration = firstItinerary.duration;
              if (!duration || duration === 'PT0H') {
                try {
                  const departureDate = parseISO(departureTime);
                  const arrivalDate = parseISO(arrivalTime);
                  const durationMs = arrivalDate.getTime() - departureDate.getTime();
                  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
                  const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                  duration = `PT${durationHours}H${durationMinutes}M`;
                } catch (e) {
                  duration = 'Não disponível';
                }
              }
              
              // Formatar duração para exibição
              const formattedDuration = duration.startsWith('PT') 
                ? duration.replace('PT', '').replace('H', 'h ').replace('M', 'm')
                : duration;
              
              return (
                <Card key={index} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-base flex items-center">
                        <Plane className="mr-2 h-4 w-4" />
                        {airlineName} {flightNumber}
                      </CardTitle>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        {stops === 0 ? 'Direto' : `${stops} ${stops === 1 ? 'escala' : 'escalas'}`}
                      </Badge>
                    </div>
                    <CardDescription className="text-xs">
                      {safeFormatDate(departureTime, "dd 'de' MMMM 'de' yyyy", 'Data não disponível')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Partida</p>
                        <p className="font-semibold">{departureAirport}</p>
                        <p>{safeFormatDate(departureTime, "HH:mm", 'Horário não disponível')}</p>
                        {firstSegment.departure.terminal && (
                          <p className="text-xs text-gray-500">Terminal {firstSegment.departure.terminal}</p>
                        )}
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Duração</p>
                          <p>{formattedDuration}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Chegada</p>
                        <p className="font-semibold">{arrivalAirport}</p>
                        <p>{safeFormatDate(arrivalTime, "HH:mm", 'Horário não disponível')}</p>
                        {lastSegment.arrival.terminal && (
                          <p className="text-xs text-gray-500">Terminal {lastSegment.arrival.terminal}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Detalhes de escalas */}
                    {stops > 0 && (
                      <div className="mt-4 border-t pt-4">
                        <p className="text-sm font-medium mb-2">Escalas:</p>
                        {firstItinerary.segments.slice(0, -1).map((segment, idx) => (
                          <div key={idx} className="text-xs mb-2">
                            <p>
                              {segment.arrival.iataCode} - 
                              {safeFormatDate(segment.arrival.at, "HH:mm", 'Horário não disponível')} até {' '}
                              {safeFormatDate(firstItinerary.segments[idx + 1].departure.at, "HH:mm", 'Horário não disponível')}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Preço */}
                    {price && price !== '0' && (
                      <div className="mt-4 border-t pt-4">
                        <p className="text-sm font-medium">Preço:</p>
                        <p className="text-lg font-bold">
                          {new Intl.NumberFormat('pt-BR', { 
                            style: 'currency', 
                            currency: currency 
                          }).format(parseFloat(price))}
                        </p>
                      </div>
                    )}
                    
                    {/* Informações adicionais para debug */}
                    <div className="mt-4 border-t pt-4 text-xs text-gray-500">
                      <details>
                        <summary className="cursor-pointer font-medium">Informações técnicas</summary>
                        <div className="mt-2 space-y-1">
                          <p>ID: {flight.id}</p>
                          <p>Fonte: {flight.source}</p>
                          <p>Número de assentos: {flight.numberOfBookableSeats}</p>
                          <p>Duração (raw): {firstItinerary.duration}</p>
                          <p>Segmentos: {firstItinerary.segments.length}</p>
                        </div>
                      </details>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {searchError && flightResults.length === 0 && (
        <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{searchError}</p>
        </div>
      )}
    </div>
  );
} 